import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import Loading from './Loading';
import './PublicReview.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PublicReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedReviewModal, setSelectedReviewModal] = useState(null);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0
    }
  });

  const { isLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('relevant');

  useEffect(() => {
    fetchStatsData();
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [currentPage, sortBy]);

  const fetchStatsData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/Reviews?pageSize=1000&sortBy=relevant`);
      
      if (response.ok) {
        const data = await response.json();
        calculateStats(data.reviews || []);
      }
    } catch (err) {
      console.error('Error fetching stats data:', err);
    }
  };

  const fetchReviews = async () => {
    if (currentPage > 1) {
      setPageLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize
      });

      switch (sortBy) {
        case 'relevant':
          params.append('sortBy', 'relevant');
          params.append('sortDescending', 'true');
          break;
        case 'newest':
          params.append('sortBy', 'updatedat');
          params.append('sortDescending', 'true');
          break;
        case 'highest':
          params.append('sortBy', 'rating');
          params.append('sortDescending', 'true');
          break;
        case 'lowest':
          params.append('sortBy', 'rating');
          params.append('sortDescending', 'false');
          break;
        default:
          params.append('sortBy', 'relevant');
          params.append('sortDescending', 'true');
      }

      const response = await fetch(`${API_BASE_URL}/Reviews?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }

      const data = await response.json();
      
      setReviews(data.reviews || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.currentPage || 1);
      setPageSize(data.pageSize || 3);
      setError(null);

    } catch (err) {
      setError(err.message);
      setReviews([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const calculateStats = (reviewsList) => {
    if (!reviewsList || reviewsList.length === 0) {
      setStats({
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
      });
      return;
    }

    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let totalRating = 0;

    reviewsList.forEach(review => {
      distribution[review.rating]++;
      totalRating += review.rating;
    });

    setStats({
      averageRating: (totalRating / reviewsList.length).toFixed(1),
      totalReviews: reviewsList.length,
      ratingDistribution: distribution
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i}>{i <= rating ? '★' : '☆'}</span>
      );
    }
    return stars;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays <= 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleWriteReview = () => {
    if (isLoggedIn) {
      navigate('/dashboard/write-review')
    } else {
      navigate('/login');
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    const actionBar = document.querySelector('.public-reviews-action-bar');
    if (actionBar) {
      actionBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getRatingPercentage = (count) => {
    if (stats.totalReviews === 0) return 0;
    const maxCount = Math.max(...Object.values(stats.ratingDistribution));
    if (maxCount === 0) return 0;
    return Math.round((count / maxCount) * 100);
  };

  const truncateText = (text, maxLength = 500) => {
    if (!text || text.length <= maxLength) {
      return { text, isTruncated: false };
    }

    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    const finalText = lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated;

    return { text: finalText + '...', isTruncated: true };
  };

  const handleShowMoreReview = (review) => {
    setSelectedReviewModal(review);
  };

  const handleCloseReviewModal = () => {
    setSelectedReviewModal(null);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleCloseImageModal = () => {
    setSelectedImage(null);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (selectedImage) {
          handleCloseImageModal();
        } else if (selectedReviewModal) {
          handleCloseReviewModal();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [selectedImage, selectedReviewModal]);

  const handleReviewModalOverlayClick = (e) => {
    if (e.target === e.currentTarget && !selectedImage) {
      handleCloseReviewModal();
    }
  };

  const handleImageModalClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseImageModal();
    }
  };

  if (loading) {
    return (
      <div className="public-reviews-container">
        <div className="public-reviews-loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-reviews-container">
        <div className="public-reviews-empty">
          <i className="fas fa-exclamation-circle"></i>
          <div className="public-reviews-empty-title">Unable to Load Reviews</div>
          <div className="public-reviews-empty-text">
            Please try again later or contact support if the problem persists.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-reviews-container">
      <div className="public-reviews-header">
        <h1 className="public-reviews-title">Customer Reviews</h1>
        <p className="public-reviews-subtitle">
          See what our customers say about Ardency Construction & Trading
        </p>
      </div>

      {/* Statistics Section */}
      {stats.totalReviews > 0 && (
        <div className="public-reviews-stats">
          <div className="public-reviews-rating-summary">
            <div className="public-reviews-average-rating">
              {stats.averageRating}
            </div>
            <div className="public-reviews-stars-large">
              {renderStars(Math.round(stats.averageRating))}
            </div>
            <div className="public-reviews-total-count">
              {stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'}
            </div>
          </div>

          <div className="public-reviews-rating-distribution">
            {[5, 4, 3, 2, 1].map(rating => (
              <div key={rating} className="public-reviews-bar-item">
                <div className="public-reviews-bar-label">
                  {rating}<span className="star">★</span>
                </div>
                <div 
                  className="public-reviews-bar"
                  title={`${stats.ratingDistribution[rating]} review${stats.ratingDistribution[rating] !== 1 ? 's' : ''} with ${rating} star${rating !== 1 ? 's' : ''}`}
                >
                  <div
                    className="public-reviews-bar-fill"
                    style={{ width: `${getRatingPercentage(stats.ratingDistribution[rating])}%` }}
                  >
                  </div>
                </div>
                <div className="public-reviews-bar-count">
                  {stats.ratingDistribution[rating]}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="public-reviews-action-bar">
        <div className="public-reviews-sort-group">
          <label className="public-reviews-sort-label">Sort By</label>
          <select
            className="public-reviews-sort-select"
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            disabled={pageLoading}
          >
            <option value="relevant">Most Relevant</option>
            <option value="newest">Most Recent</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>

        {isLoggedIn && (
          <button
            className="public-reviews-write-btn"
            onClick={handleWriteReview}
          >
            <i className="fas fa-pen"></i>
            Write a Review
          </button>
        )}
      </div>

      {pageLoading && (
        <div className="public-reviews-loading">
          <Loading />
        </div>
      )}

      {/* Reviews List */}
      {!pageLoading && (
        <div className="public-reviews-list">
          {reviews.length > 0 ? (
            reviews.map((review) => (
              <div key={review.reviewID} className="public-review-card">
                <div className="public-review-header">
                  <div className="public-review-user-info">
                    <div className="public-review-user-details">
                      <div className="public-review-username">
                        {review.author?.firstName} {review.author?.lastName || ''}
                      </div>
                      <div className="public-review-date">
                        {formatDate(review.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="public-review-rating">
                    <div className="public-review-stars">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                </div>

                <div className="public-review-content">
                  <h3 className="public-review-title">{review.title}</h3>
                  <p className="public-review-text">
                    {(() => {
                      const { text, isTruncated } = truncateText(review.content);
                      return (
                        <>
                          {text}
                          {isTruncated && (
                            <button
                              className="public-review-more-btn"
                              onClick={() => handleShowMoreReview(review)}
                            >
                              More
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </p>

                  {review.images && review.images.length > 0 && (
                    <div className="public-review-images">
                      {review.images.map((image) => (
                        <img
                          key={image.imageId}
                          src={image.imageUrl}
                          alt={`Review image ${image.displayOrder}`}
                          className="public-review-image"
                          onClick={() => handleImageClick(image.imageUrl)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="public-reviews-empty">
              <i className="fas fa-comments"></i>
              <div className="public-reviews-empty-title">No Reviews Yet</div>
              <div className="public-reviews-empty-text">
                {isLoggedIn
                  ? "Be the first to share your experience with us!"
                  : "Login to be the first to share your experience!"}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !pageLoading && (
        <div className="public-reviews-pagination">
          <button
            className="public-reviews-pagination-btn"
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            className="public-reviews-pagination-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {[...Array(totalPages).keys()].map((number) => {
            if (
              number + 1 === 1 ||
              number + 1 === totalPages ||
              (number + 1 >= currentPage - 1 && number + 1 <= currentPage + 1)
            ) {
              return (
                <button
                  key={number + 1}
                  className={`public-reviews-pagination-btn ${currentPage === number + 1 ? 'active' : ''
                    }`}
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </button>
              );
            }

            if (
              (number + 1 === currentPage - 2 && currentPage > 3) ||
              (number + 1 === currentPage + 2 && currentPage < totalPages - 2)
            ) {
              return (
                <span key={number + 1} style={{ padding: '0 5px' }}>...</span>
              );
            }
            return null;
          })}

          <button
            className="public-reviews-pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            className="public-reviews-pagination-btn"
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      )}

      {/* Review Detail Modal */}
      {selectedReviewModal && (
        <div
          className={`public-review-modal-overlay ${selectedImage ? 'dimmed' : ''}`}
          onClick={handleReviewModalOverlayClick}
        >
          <div className="public-review-modal">
            <div className="public-review-modal-header">
              <button
                className="public-review-modal-close"
                onClick={handleCloseReviewModal}
              >
                <i className="fas fa-times"></i>
              </button>

              <div className="public-review-modal-user-info">
                <div className="public-review-modal-user-details">
                  <h4>
                    {selectedReviewModal.author?.firstName} {selectedReviewModal.author?.lastName || ''}
                  </h4>
                  <p className="public-review-modal-date">
                    {formatDate(selectedReviewModal.createdAt)}
                  </p>
                </div>
              </div>

              <div className="public-review-modal-rating">
                <div className="public-review-modal-stars">
                  {renderStars(selectedReviewModal.rating)}
                </div>
              </div>
            </div>

            <div className="public-review-modal-content">
              <h3 className="public-review-modal-title">
                {selectedReviewModal.title}
              </h3>
              <p className="public-review-modal-text">
                {selectedReviewModal.content}
              </p>

              {selectedReviewModal.images && selectedReviewModal.images.length > 0 && (
                <div className="public-review-modal-images">
                  {selectedReviewModal.images.map((image) => (
                    <img
                      key={image.imageId}
                      src={image.imageUrl}
                      alt={`Review image ${image.displayOrder}`}
                      className="public-review-modal-image"
                      onClick={() => handleImageClick(image.imageUrl)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="public-reviews-image-modal"
          onClick={handleImageModalClick}
        >
          <img src={selectedImage} alt="Review preview" />
        </div>
      )}
    </div>
  );
};

export default PublicReviews;